import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  buildRole10ColumnKey,
  isRole10Empty,
  isRole10StaticallyRequired,
  pickRole10Schema,
  resolveRole10VisibleSuffixes,
  Role10Schema,
  Role10SchemaControl,
} from './role10-sofp-liquidity.rules';

/**
 * Pure validator for Role 10 (Statement of Financial Position by Order of
 * Liquidity).
 *
 * ---- Parity target -------------------------------------------------------
 *
 * The role 10 component publishes its sheet-level status EXACTLY ONCE per
 * mount, via:
 *
 *     this.stateService.markSheetInitializedWithStatus(
 *       'statementOfFinancialPositionLiquidity',
 *       false,
 *     );
 *
 * (component L474, L484). There is no `evaluateSheetStatus()` method, no
 * post-init `updateSheetLevelStatus` call, and no valueChanges-driven
 * status push. The published `hasErrors` for this sheet is therefore
 * deterministically `false` regardless of the form's actual validity.
 *
 * To keep the dev-only ParityCheckService from throwing, this validator
 * MUST mirror that and return `hasErrors: false`. Returning the schema-
 * accurate result (e.g. flagging empty required cells) would trip the
 * harness on every fresh conversion.
 *
 * ---- Latent bug LB-014 ---------------------------------------------------
 *
 * Schema declares `required: true` on rows like CashAndCashEquivalentsLiquidity,
 * TradeReceivablesLiquidity, PropertyPlantAndEquipmentLiquidity, and the
 * four calculated totals. The component's `buildForm()` wires
 * `Validators.required` and `Validators.min` on these — `form.invalid`
 * does flip while the user has empty cells, but that flag never reaches
 * `updateSheetLevelStatus`. The `submitForm()` path checks
 * `this.statementOfFinancialPositionLiquidityForm.invalid` and surfaces a
 * toast, but does not publish the result to the state service.
 *
 * This is documented as LB-014 in the latent-bugs ledger. The validator
 * carries the LB-001 fix-guard via `isRole10StaticallyRequired` — the day
 * the editor is fixed to actually publish row errors, only the
 * `evaluateRowErrors()` call below needs flipping from a guarded no-op
 * back into the active result; the schema scan is already in place.
 *
 * ---- Cross-sheet posture -------------------------------------------------
 *
 * Tier 4 per the implementation plan because:
 *
 *   • Calculations: `calculateTotals()` derives 4 row totals
 *     (TotalAssetsLiquidity, TotalLiabilitiesLiquidity, TotalEquityLiquidity,
 *     TotalEquityAndLiabilitiesLiquidity) per visible column suffix.
 *   • Cross-sheet: `checkConsolidatedStatus()` reads `scopeOfFiling` to
 *     decide column visibility (1 / 2 / 4 columns).
 *
 * Neither piece influences `hasErrors` today (because the editor doesn't
 * publish row errors at all — see above). The validator still:
 *
 *   • Reads `context.crossSheetData?.get('scopeOfFiling')` defensively to
 *     resolve the visible column suffixes — used by the guarded row-error
 *     scan so a future fix can plug in without re-threading the interface.
 *   • Reads (and currently ignores) `filingPrerequisites` for parity with
 *     the Tier 4 contract — role 10 doesn't read it directly today, but
 *     `SheetDependencyService` lists it as a global dependency so the
 *     orchestrator may include it in the bundle.
 *
 * Both reads tolerate an absent slice (defaults flow through to the same
 * single-column layout the component renders pre-load).
 *
 * ---- Calculations note ---------------------------------------------------
 *
 * The pure validator does NOT replay `calculateTotals()` — the component
 * persists post-calculation values via `setValue(...)` AND the autosave
 * pipeline writes them to state. By the time the orchestrator reads
 * `sheetData`, the totals are already present. This mirrors the role 9
 * validator's "read the persisted post-calculation values from sheetData
 * directly" stance (role9-sub-classification.validator.ts L246-254).
 */

function isSectionHeader(control: Role10SchemaControl): boolean {
  return (
    control.type === 'SectionHeader' || control.ui?.control === 'header'
  );
}

/**
 * Walk every required row × visible column key. Today this returns an
 * empty result because LB-014 is preserved (see header) — but the body is
 * kept intentionally so that flipping the latent bug becomes a one-line
 * change at the call site, not a re-derivation of the row/column
 * expansion.
 *
 * Mirrors the row-level "all visible columns empty -> Failed" branch the
 * role 8 / role 13 components use, adapted to role 10's flat-suffix key
 * shape (e.g. `qname + 'GroupCurrent'`, not `qname + '[Group][Current]'`).
 */
function evaluateRowErrors(
  schema: Role10Schema,
  sheetData: Record<string, any>,
  scopeOfFiling: Record<string, unknown> | undefined
): Record<string, string> {
  const errors: Record<string, string> = {};
  const suffixes = resolveRole10VisibleSuffixes(scopeOfFiling);

  for (const control of schema.controls) {
    if (isSectionHeader(control)) continue;
    if (!isRole10StaticallyRequired(control)) continue;
    // Calculated rows are populated by the component's calculateTotals()
    // after valueChanges settle. They carry `required: true` in the
    // schema (e.g. TotalAssetsLiquidity) but the component disables them
    // in `disableCalculatedFields()` so `Validators.required` never
    // contributes to `form.invalid`. Skip them here so the validator
    // never flags a calculated row as missing.
    if (control.ui?.calculated || control.ui?.readonly) continue;

    if (control.ui?.dualColumn) {
      // Row is missing only when EVERY visible column is empty —
      // matches the row-level evaluation the editor would run if it
      // were publishing row errors today.
      const anyFilled = suffixes.some(
        (suffix) => !isRole10Empty(sheetData[buildRole10ColumnKey(control.qname, suffix)])
      );
      if (!anyFilled) {
        errors[control.qname] = `${control.qname} is required`;
      }
    } else {
      // Single-column control (the explanatory textarea is the only
      // non-dualColumn control in the current schema, and it isn't
      // required — but kept for symmetry with the dualColumn branch).
      if (isRole10Empty(sheetData[control.qname])) {
        errors[control.qname] = `${control.qname} is required`;
      }
    }
  }

  return errors;
}

export const role10SofpLiquidityValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfFinancialPositionLiquidity,
  // Tier 4 per phase-2 plan: cross-sheet reads (scopeOfFiling for column
  // visibility) + calculations (4 row totals × N visible suffixes). Today
  // neither contributes to the published hasErrors (LB-014), but the
  // classification holds because a future fix flipping the editor to
  // publish row errors would activate both pieces simultaneously.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    // Defensive cross-sheet reads — both slices may be absent on the
    // cache-miss path or mid-init. The orchestrator's
    // SheetDependencyService bundles `scopeOfFiling` and (via the
    // global filingPrerequisites entry) the prerequisites slice for
    // role 10's dependents, but parity-side this validator must
    // tolerate any subset.
    const scopeOfFiling = context.crossSheetData?.get('scopeOfFiling') as
      | Record<string, unknown>
      | undefined;

    // filingPrerequisites is read defensively for surface symmetry with
    // other Tier 4 validators. Role 10's component does not consume it
    // directly today, but SheetDependencyService treats Role 0 as a
    // global config hub — a future cross-sheet rule (e.g. layout driven
    // by accountingStandard) would plug in here without re-threading
    // the interface. Cast `void` to silence the unused-variable lint
    // while keeping the read documented.
    const _filingPrerequisites = context.crossSheetData?.get(
      'filingPrerequisites'
    );
    void _filingPrerequisites;

    const schema = pickRole10Schema(context.accountingStandard);

    // Developer Note (TODO): Role-9-inheritance parity gap — DORMANT here.
    // Role 10 is the by-liquidity analog of Role 8 and inherits its leaf line
    // items from Role 11 (sub-classification liquidity) via `ui.linkTo` at
    // mount time (pullRole11Values-style), exactly as Role 8 inherits from
    // Role 9. When this validator is activated (the LB-014 ledger-flip below),
    // any blocking equality / self-balance check MUST first replay that
    // Role-11 leaf inheritance before recomputing totals — otherwise it will
    // hit the same "green tick on the listing → red cross on open" parity bug
    // that was fixed in Role 8 and Role 12. Use the shared
    // `applyLinkedInheritance(schema.controls, sheetData, role11Slice, '<role11 link role>')`
    // helper in calc-diff.helper.ts and feed its output to the equality
    // recompute (NOT the Validated* diff, which keeps the raw persisted slice).
    // Left dormant only because Role 10 / Role 11 are not in active use yet
    // (like Role 14).

    // LB-014: the editor publishes hasErrors=false unconditionally, so
    // the validator must too. The row-error scan below is computed but
    // intentionally NOT folded into the result — it stays as a guarded
    // no-op so a future ledger-flip from `🟰 PRESERVED` to `🔧 FIXED`
    // becomes a one-line change (replace the constant `false` with
    // `Object.keys(errors).length > 0`).
    const errors = evaluateRowErrors(schema, sheetData, scopeOfFiling);
    void errors;

    return {
      hasErrors: false,
      // Developer Note (TODO): stub — `hasSuggestedChanges` is hard-coded false,
      // so this sheet's dropdown "*" can never light. When released as a live
      // monetary sheet, mirror role13: (1) recompute via
      // CalculationService.calculateTotals_<id> + hasCalculatedFieldMismatch(
      // context.extractedBaseline ?? sheetData, recomputed); (2) add its sheet key
      // to PURE_VERDICT_SHEETS; (3) call evaluateSheetStatus() in the component's
      // runAllValidations(); (4) add its sheet key to CALC_DIFF_SHEETS in
      // suggestion-capability.ts, or the "(*) Optional: Suggestions to refine
      // data" legend will stay hidden on this sheet even once its "*" can light.
      hasSuggestedChanges: false,
    };
  },
};
